import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const TermsSection = ({ title, description }) => (
  <Box>
    <Typography
      variant="h6"
      gutterBottom
      sx={{
        fontWeight: 'bold',
      }}
    >
      {title}
    </Typography>
    {description}
  </Box>
);

TermsSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.object.isRequired,
};

const Paragraph = ({ children }) => (
  <Typography component="p" color="textSecondary" sx={{ textAlign: 'justify', mb: 1.5 }}>
    {children}
  </Typography>
);

Paragraph.propTypes = {
  children: PropTypes.node.isRequired,
};

const List = ({ children }) => (
  <Typography
    component="ul"
    color="textSecondary"
    sx={{ textAlign: 'justify', mb: 1.5, pl: 3, '& li': { mb: 1 } }}
  >
    {children}
  </Typography>
);

List.propTypes = {
  children: PropTypes.node.isRequired,
};

const PrivacyContent = () => {
  const privacyData = [
    {
      title: '1. WHO WE ARE',
      description: (
        <Box>
          <Paragraph>
            <b>1.1</b> The Application is operated by Cartify Portal FZCO and its licensee in
            Lebanon, INNOV SARL (together <b>“we,” “us,” “our”</b>). References to the{' '}
            <b>“Provider”</b> in the Terms and Conditions have the same meaning here.
          </Paragraph>
          <Paragraph>
            <b>1.2</b> This Privacy Policy applies to you whether you use the Application as a food
            and beverage supplier (a <b>“Supplier”</b>), as an operator such as a restaurant (an{' '}
            <b>“Operator”</b>), or as an Authorized User of either. In this Privacy Policy{' '}
            <b>“you”</b> and <b>“your”</b> refer to the individual whose personal information is
            processed.
          </Paragraph>
          <Paragraph>
            <b>1.3</b> Under no circumstances shall Cartify Portal FZCO and its licensees be deemed
            jointly or severally liable towards you on any grounds.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '2. THE LAW THAT APPLIES',
      description: (
        <Box>
          <Paragraph>
            <b>2.1</b> This Privacy Policy is governed by the laws of Lebanon, and in particular Law
            No. 81 of 2018 on Electronic Transactions and Personal Data.
          </Paragraph>
          <Paragraph>
            <b>2.2</b> Where any other data protection law applies to a particular user or
            processing activity, we will comply with it to the extent it applies. References in this
            Privacy Policy to legal concepts such as controller, consent, legitimate interest and
            data subject rights are to be understood in the sense given to them under Applicable
            Laws.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '3. WHO CONTROLS YOUR DATA',
      description: (
        <Box>
          <Paragraph>
            <b>3.1</b> It is important to understand who is responsible for your personal
            information at each stage, because the Application connects two independent businesses.
          </Paragraph>
          <Paragraph>
            <b>3.2 We are responsible</b> for the personal information we collect to operate the
            Application, namely account and registration data, information about how you use the
            Application, technical data, and the data we use for advertising and analytics as
            described in this Privacy Policy.
          </Paragraph>
          <Paragraph>
            <b>3.3 Suppliers and Operators are each independently responsible</b> for the personal
            information they process about one another once the Application connects them, including
            any personal information exchanged in the course of placing, confirming, delivering and
            paying for an order. We are not responsible for, and have no control over, how a
            Supplier or an Operator processes personal information for its own purposes, including
            any non-compliant processing. Each Supplier and Operator, and their Authorized Customer
            Entities and Authorized Users, is solely responsible for complying with its own
            obligations under Applicable Laws in respect of such processing.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '4. WHAT WE COLLECT',
      description: (
        <Box>
          <Paragraph>We collect the following categories of personal information:</Paragraph>
          <Paragraph>
            <b>4.1 Account information.</b> When you register, we collect your name, your company’s
            legal name, your business address, email address, phone number, job title, and the
            identification details needed to set up and verify your account.
          </Paragraph>
          <Paragraph>
            <b>4.2 Order and platform activity.</b> We collect information about your use of the
            Application, including the orders you place or receive, your product listings and
            catalogue data (for Suppliers), your browsing and search activity, items added to a
            cart, and your order history.
          </Paragraph>
          <Paragraph>
            <b>4.3 Technical information.</b> We automatically collect technical data when you use
            the Application, including your IP address, device information, approximate location
            derived from your IP address for the purpose of identifying your country or city,
            session data such as page response times and interactions, and information collected
            through cookies and similar technologies.
          </Paragraph>
          <Paragraph>
            <b>4.4 Communications.</b> We collect the content of communications you send to us, for
            example when you contact support.
          </Paragraph>
          <Paragraph>
            <b>4.5 Payment information.</b> We do not currently collect or process payment card
            data, because payment for orders takes place directly between Operators and Suppliers
            outside the Application. If in-app payment is introduced in the future, we will collect
            and process payment and transaction data only from that point, and we will update this
            Privacy Policy to describe how that data is handled and secured before any such feature
            goes live.
          </Paragraph>
          <Paragraph>
            <b>4.6</b> You are responsible for ensuring that all personal information you provide to
            us is accurate, complete, current and not misleading, and you shall promptly update it
            whenever changes occur so that the information we hold remains accurate and up to date.
            Please do not submit any content that is not requested through the relevant form or
            collection method.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '5. HOW WE USE YOUR INFORMATION AND OUR LEGAL BASIS',
      description: (
        <Box>
          <Paragraph>
            <b>5.1 To provide the Services (performance of the Agreement).</b> We process your
            account, order and platform activity data because we cannot provide the Application to
            you without it. This includes registering and maintaining your account, transmitting
            Operators’ purchase orders to Suppliers, confirming order details between the parties,
            and providing support. This processing is necessary for the performance of the Agreement
            and does not depend on your separate consent.
          </Paragraph>
          <Paragraph>
            <b>5.2 To operate, secure and improve the Application (legitimate interest).</b> We
            process technical and usage data to keep the Application working and secure, to prevent
            fraud, to diagnose problems, and to analyse and improve the Services, including through
            aggregated statistics that do not identify you.
          </Paragraph>
          <Paragraph>
            <b>5.3 For advertising and promoted offers (condition of use).</b>
          </Paragraph>
          <Paragraph>
            <b>5.3.1</b> Advertising, sponsored listings and promoted offers may form part of the
            Services provided through the Application.
          </Paragraph>
          <Paragraph>
            <b>5.3.2</b> Subject to Applicable Laws, we may use information relating to your
            activity on the Application, including browsing, search and order history, to select,
            target, measure and improve advertising and promotional content shown to you.
          </Paragraph>
          <Paragraph>
            <b>5.3.3</b> Customers may separately purchase advertising or promoted placement through
            the Application. The Provider may process the information necessary to administer,
            deliver and measure such advertising services.
          </Paragraph>
          <Paragraph>
            <b>5.3.4</b> The purchase of advertising does not give an advertiser access to the
            personal information, order data, prices, catalogue data or other Confidential
            Information of other Customers. We will only disclose personal information where
            permitted by Applicable Laws and where the applicable legal basis and safeguards are
            satisfied.
          </Paragraph>
          <Paragraph>
            <b>5.3.5</b> Where Applicable Laws require consent or provide a right to object or opt
            out of particular advertising-related processing, we will provide and respect the
            applicable mechanism.
          </Paragraph>
          <Paragraph>
            <b>5.4 For marketing communications.</b> We may send you promotional communications
            about the Application and related offers. Every marketing email we send includes a means
            to unsubscribe, and you may stop receiving marketing communications at any time by using
            it. Unsubscribing from marketing communications does not affect the advertising shown to
            you within the Application, which is governed by Article 5.3, nor does it affect service
            and administrative messages we need to send you to operate your account.
          </Paragraph>
          <Paragraph>
            <b>5.5 To comply with law and protect our rights.</b> We process personal information
            where necessary to comply with a legal obligation, to respond to lawful requests from
            authorities, to establish, exercise or defend legal claims, and to protect the security
            and integrity of the Application.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '6. COOKIES',
      description: (
        <Box>
          <Paragraph>
            <b>6.1</b> We use cookies and similar technologies to operate the Application, to
            remember your preferences, to analyse how the Application is used, and to support the
            advertising described in Article 5.3.
          </Paragraph>
          <Paragraph>
            <b>6.2</b> The cookies we use fall into three categories: essential cookies, which are
            necessary for the Application to function, including for login and security; functional
            cookies, which remember your preferences; and analytical and advertising cookies, which
            help us understand usage and deliver the advertising described in this Privacy Policy.
            Some cookies are placed by third-party analytics providers, whose own privacy policies
            govern their use of data.
          </Paragraph>
          <Paragraph>
            <b>6.3</b> By accepting this Privacy Policy and using the Application, you consent to
            our use of cookies as described here. Essential cookies cannot be disabled, as the
            Application cannot function without them.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '7. HOW WE SHARE YOUR INFORMATION',
      description: (
        <Box>
          <Paragraph>
            <b>7.1 We do not sell or rent your personal information.</b>
          </Paragraph>
          <Paragraph>
            <b>7.2 Between Suppliers and Operators.</b> To operate the marketplace, we make a
            Supplier’s product listings and prices visible to Operators as potential buyers, and we
            share the order and contact details necessary for an Operator and a Supplier to complete
            an order between them.
          </Paragraph>
          <Paragraph>
            <b>7.3 Service providers.</b> We share personal information with third parties who help
            us provide and maintain the Application (our <b>“service providers”</b>), only to the
            extent necessary for them to perform their function. We bind our service providers by
            contract to data protection obligations consistent with this Privacy Policy and to
            appropriate security measures, and they may not use your information for their own
            purposes.
          </Paragraph>
          <Paragraph>
            <b>7.4 Within our group.</b> We may share your information between Cartify Portal FZCO,
            its licensees including INNOV SARL, and their affiliates, for the purposes described in
            this Privacy Policy.
          </Paragraph>
          <Paragraph>
            <b>7.5 Legal and government requests.</b> We may disclose your personal information
            where such disclosure is required or permitted by Applicable Laws, court order, judicial
            proceedings, regulatory authority, governmental agency or any competent authority. We
            may also disclose personal information where we reasonably believe it is necessary to
            protect or enforce our legal rights, to protect the rights, property or safety of the
            Provider, users, Suppliers, Operators or the public, to prevent fraud, to investigate
            security incidents, or to comply with legal obligations.
          </Paragraph>
          <Paragraph>
            <b>7.6 Business transfers.</b> In the event of any merger, acquisition, restructuring,
            financing transaction, change of control, joint venture, or sale, transfer or disposal
            of all or part of our business, assets or operations, your personal information may be
            transferred as part of such transaction. Any such transfer shall remain subject to this
            Privacy Policy or a privacy policy providing substantially equivalent protection for
            your personal information.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '8. INTERNATIONAL TRANSFERS',
      description: (
        <Box>
          <Paragraph>
            <b>8.1</b> We operate through Cartify Portal FZCO in the United Arab Emirates and INNOV
            SARL in Lebanon, and we may use service providers located outside Lebanon. As a result,
            your personal information may be transferred to and processed in countries other than
            Lebanon.
          </Paragraph>
          <Paragraph>
            <b>8.2</b> Where we transfer your personal information across borders, we take steps to
            ensure it remains protected in accordance with this Privacy Policy and Applicable Laws,
            including by binding the recipients to appropriate confidentiality and security
            obligations.
          </Paragraph>
          <Paragraph>
            <b>8.3</b> Where required by Applicable Laws, we will implement appropriate safeguards
            in connection with international transfers of personal information, which may include
            contractual obligations imposed on the recipient, confidentiality requirements,
            appropriate technical and organizational security measures, restrictions on onward
            transfers, and any other legally recognized safeguards applicable to the relevant
            transfer.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '9. HOW LONG WE KEEP YOUR INFORMATION',
      description: (
        <Box>
          <Paragraph>
            <b>9.1</b> We keep your personal information only for as long as necessary for the
            purposes described in this Privacy Policy, according to the following periods:
          </Paragraph>
          <List>
            <li>
              (a) account and registration data, for as long as your account is active and for
              twelve (12) months after it is closed;
            </li>
            <li>
              (b) order records and related transaction data, for the period required by applicable
              tax and commercial law;
            </li>
            <li>
              (c) technical, usage and advertising data, for a rolling period after collection,
              after which it is deleted or retained only in aggregated, non-identifying form; and
            </li>
            <li>
              (d) communications with support, for as long as necessary to resolve your matter and
              for a reasonable period afterwards.
            </li>
          </List>
          <Paragraph>
            <b>9.2</b> We may retain personal information for longer where required to comply with a
            legal obligation or to establish, exercise or defend legal claims. When you close your
            account, some limited identification and activity records may remain in our systems for
            the periods described above.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '10. YOUR RIGHTS',
      description: (
        <Box>
          <Paragraph>
            <b>10.1</b> Subject to Applicable Laws, you have the right to access your personal
            information, to obtain a copy of it, to have inaccurate information corrected and
            incomplete information completed, to request erasure of your information, to object to
            certain processing, and to withdraw any consent you have given, without affecting the
            lawfulness of processing carried out before withdrawal.
          </Paragraph>
          <Paragraph>
            <b>10.2</b> Some of these rights are not absolute. We may retain or continue to process
            personal information where we have a legal obligation or a legitimate ground to do so
            under Applicable Laws, including to provide the Services you continue to use.
          </Paragraph>
          <Paragraph>
            <b>10.3</b> Where the personal information in question is processed by a Supplier or an
            Operator for its own purposes rather than by us, you should exercise your rights
            directly against that Supplier or Operator, which is independently responsible for that
            processing as described in Article 3.
          </Paragraph>
          <Paragraph>
            <b>10.4</b> To exercise your rights, contact us using the details in Article 14. We may
            ask you for information to verify your identity before acting on your request, and we
            will respond within the timeframes set by Applicable Laws.
          </Paragraph>
          <Paragraph>
            <b>10.5</b> If you believe that your personal information has been processed in
            violation of this Privacy Policy or Applicable Laws, you may contact us using the
            details in Article 14. We will review and respond to your request or complaint in
            accordance with Applicable Laws, without prejudice to your right to refer the matter to
            the competent regulatory or judicial authorities where applicable.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '11. SECURITY',
      description: (
        <Box>
          <Paragraph>
            <b>11.1</b> We take appropriate technical and organisational measures to protect your
            personal information, including encryption of data in transit, access controls limiting
            access to our personnel on a need-to-know basis, and measures designed to ensure the
            confidentiality, integrity and availability of our systems.
          </Paragraph>
          <Paragraph>
            <b>11.2</b> While we implement appropriate security measures, no method of electronic
            transmission, communication or storage is completely secure. You acknowledge and accept
            the inherent risks associated with the transmission and storage of information through
            the internet and electronic systems, and you are responsible for keeping your account
            credentials secure and for signing out after using the Application.
          </Paragraph>
          <Paragraph>
            <b>11.3</b> Where a personal data breach is likely to result in a high risk to your
            rights, we will notify you without undue delay, and we will notify the competent
            authority where required by Applicable Laws.
          </Paragraph>
          <Paragraph>
            <b>11.4</b> To the maximum extent permitted by Applicable Laws, we shall not be liable
            or responsible for any unauthorised access, loss, disclosure, alteration, destruction or
            compromise of personal information resulting from circumstances beyond our reasonable
            control, including cyberattacks, malicious acts or omissions of third parties, failures
            of telecommunications networks or internet infrastructure, natural disasters, acts of
            governmental authorities or other force majeure events, provided that we have
            implemented reasonable technical and organisational safeguards appropriate to the nature
            of the personal information processed. Nothing in this Article shall exclude or limit
            any obligation imposed on us under Applicable Laws in relation to the investigation,
            notification, remediation or mitigation of a personal data breach.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '12. AUTOMATED DECISION-MAKING',
      description: (
        <Box>
          <Paragraph>
            We do not make decisions producing legal effects or similarly significant effects
            concerning you solely through automated processing of your personal information. Where
            automated tools or technologies are used, they are implemented for operational,
            analytical, security or service improvement purposes and do not replace human
            decision-making where such decisions may significantly affect your rights or interests.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '13. CHILDREN',
      description: (
        <Box>
          <Paragraph>
            The Application is a business-to-business platform intended for use only by businesses
            and by individuals who are at least eighteen (18) years old. We do not knowingly collect
            personal information from anyone under eighteen. If we become aware that we have
            collected such information, we will delete it.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '14. HOW TO CONTACT US',
      description: (
        <Box>
          <Paragraph>
            <b>
              14.1 If you have any question, request or concern regarding this Privacy Policy or
              your personal information, contact us at{' '}
              <a href="mailto:support@cartify.org" target="_blank" rel="noreferrer">
                support@cartify.org
              </a>
              .
            </b>
          </Paragraph>
          <Paragraph>
            <b>14.2</b> We will use reasonable efforts to respond to and resolve your request
            without undue delay. We may ask you to provide additional information to verify your
            identity.
          </Paragraph>
        </Box>
      ),
    },
    {
      title: '15. CHANGES TO THIS PRIVACY POLICY',
      description: (
        <Box>
          <Paragraph>
            <b>15.1</b> We may update this Privacy Policy from time to time. Where a change
            materially affects how we use or disclose personal information you have already
            provided, we will give you notice through the Application or by email before the change
            takes effect, and where required by Applicable Laws we will seek your consent.
          </Paragraph>
          <Paragraph>
            <b>15.2</b> Changes that do not materially affect your rights take effect when published
            in the Application. We encourage you to review this Privacy Policy periodically.
          </Paragraph>
          <Paragraph>
            <b>15.3</b> To the maximum extent permitted by Applicable Laws, the Provider shall not
            be liable or responsible for any amendment, modification, update or change to this
            Privacy Policy, or for any consequence arising solely from such amendment, modification,
            update or change, where the same is made in accordance with this Section 15, is
            communicated or published as required herein, and does not unlawfully diminish any
            rights or protections afforded to you under Applicable Laws. Nothing in this Section
            shall exclude or limit any liability, duty or obligation that cannot lawfully be
            excluded or limited under Applicable Laws.
          </Paragraph>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        Privacy Policy
      </Typography>
      <Box marginBottom={4}>
        <Paragraph>
          This Privacy Policy explains how personal information is collected, used, shared and
          protected in connection with the CARTIFY PORTAL mobile application (the{' '}
          <b>“Application”</b>) and the Services provided through it. It forms part of the Agreement
          and should be read together with the Terms and Conditions. By accepting this Privacy
          Policy at registration and by using the Application, you agree to the collection and use
          of your personal information as described here. If you do not agree, you should not use
          the Application.
        </Paragraph>
      </Box>
      {privacyData.map((item, i) => (
        <Box key={i} marginBottom={i < privacyData.length - 1 ? 4 : 0}>
          <TermsSection {...item} />
        </Box>
      ))}
    </Box>
  );
};

export default PrivacyContent;
